import {
    AnimationComponent,
    Material,
    MeshComponent,
    Object3D,
    Animation,
} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {SpecialPower} from '../special-power.js';
import {BoardObject} from '../board-object.js';
import {Puzzle} from '../puzzle.js';
import {ScoreType} from '../score-controller.js';
import {QuickPlayGameRules} from '../rules/quick-play-game-rules.js';
import {RNG} from '../../utils/rng.js';
import {BlobPrefabs} from '../blob-prefabs.js';
import {quat, vec3} from 'gl-matrix';
import {Sounds} from '../../enums/Sounds.js';
import {AudioDirector} from '../audio-director.js';
import {FXPrefabs} from '../fx-prefabs.js';
import {Statistics} from '../statistics.js';
import {StatNames} from '../../enums/StatNames.js';
import {WLUtils} from '../../utils/WLUtils.js';

export class BonusCountdownPower extends SpecialPower {
    static TypeName = 'bonus-countdown-power';
    static InheritProperties = true;

    @property.int(1000)
    baseWorth = 1000;

    @property.float(1)
    increaseMultipler = 1;

    @property.int(11)
    explodeAfterTurnMin = 11;

    @property.int(11)
    explodeAfterTurnMax = 11;

    @property.material()
    countDownTexture15?: Material;
    @property.material()
    countDownTexture14?: Material;
    @property.material()
    countDownTexture13?: Material;
    @property.material()
    countDownTexture12?: Material;
    @property.material()
    countDownTexture11?: Material;
    @property.material()
    countDownTexture10?: Material;
    @property.material()
    countDownTexture09?: Material;
    @property.material()
    countDownTexture08?: Material;
    @property.material()
    countDownTexture07?: Material;
    @property.material()
    countDownTexture06?: Material;
    @property.material()
    countDownTexture05?: Material;
    @property.material()
    countDownTexture04?: Material;
    @property.material()
    countDownTexture03?: Material;
    @property.material()
    countDownTexture02?: Material;
    @property.material()
    countDownTexture01?: Material;
    @property.material()
    countDownTexture00?: Material;

    @property.object({required: true})
    countDownObject!: Object3D;

    @property.string('')
    bonusTextObject = '';

    @property.object({required: true})
    animatedObject!: Object3D;

    @property.animation()
    idle?: Animation;

    @property.string('BonusCollected')
    deathFX = 'BonusCollected';

    @property.string('BonusUncollected')
    lostPowerupFX = 'BonusUncollected';

    private _explodeAfterTurn;
    private _countDownMesh: MeshComponent;
    private _countDownMaterials: Material[];

    // public MeshRenderer countdownTexture;
    // public GameObject lostPowerupFX;

    @property.enum(Object.keys(Sounds).filter((e) => isNaN(Number(e))))
    detonateSFX: Sounds;

    @property.enum(Object.keys(Sounds).filter((e) => isNaN(Number(e))))
    destroySFX: Sounds;

    @property.enum(Object.keys(Sounds).filter((e) => isNaN(Number(e))))
    superStarSFX: Sounds;

    @property.enum(Object.keys(Sounds).filter((e) => isNaN(Number(e))))
    spawnSFX: Sounds;

    private _boardObject: BoardObject = null;
    private _remainingTurns: number;
    private _loadedTurn = -1;

    @property.int(16)
    columnSize = 16; // how many frames in animation

    private _numberOfTurns = 0;

    private _givePoints = true;
    private _animation: AnimationComponent;

    get countdownTime() {
        return this._remainingTurns;
    }

    start(): void {
        // 	size = new Vector2(1.0f / columnSize, 1.0f / rowSize);
        this._animation = this.animatedObject.getComponent(AnimationComponent);

        this._animation.animation = this.idle.retarget([
            this.animatedObject,
            this.animatedObject,
        ]);

        this._animation.play();

        this._countDownMaterials = [
            this.countDownTexture00,
            this.countDownTexture01,
            this.countDownTexture02,
            this.countDownTexture03,
            this.countDownTexture04,
            this.countDownTexture05,
            this.countDownTexture06,
            this.countDownTexture07,
            this.countDownTexture08,
            this.countDownTexture09,
            this.countDownTexture10,
            this.countDownTexture11,
            this.countDownTexture12,
            this.countDownTexture13,
            this.countDownTexture14,
            this.countDownTexture15,
        ];

        const rules = Puzzle.instance.rules as QuickPlayGameRules;

        this._explodeAfterTurn = RNG.getUniformInt(
            this.explodeAfterTurnMin,
            this.explodeAfterTurnMax + 1
        );

        // 	//Add any extra time from perk
        this._explodeAfterTurn += rules.getExtraBonusTime;

        this._numberOfTurns = this.columnSize - this._explodeAfterTurn;
        this._countDownMesh = this.countDownObject.getComponent(MeshComponent);

        this._updateTextureOffset();

        // 	audioDirector = AudioDirector.GetDirector();

        AudioDirector.instance.playOneShot(this.spawnSFX, true);
    }

    // public void Update()
    // {
    // 	UpdateTextureOffset();
    // }

    override activate(puzzle: Puzzle, row: number, column: number) {}

    override popped(puzzle: Puzzle, row: number, column: number) {}

    override loaded(newParent: BoardObject) {
        if (this._loadedTurn == -1) {
            this._boardObject = newParent;
            this._loadedTurn = Puzzle.instance.rules.turnsSinceStart;
            this._remainingTurns = this._explodeAfterTurn;
        }
    }

    override explode(puzzle: Puzzle, row: number, column: number) {
        //superstar!!!
        if (this._remainingTurns == this._explodeAfterTurn) {
            const targetCamera = this.engine.scene.activeViews[0].object;
            const scoreFX = FXPrefabs.instance.spawn(this.bonusTextObject, targetCamera);
            const p = this._boardObject.object.getPositionWorld();
            scoreFX.setPositionWorld(p);
            WLUtils.setActive(scoreFX, true);
            //Statistics.instance.recordAdditive(StatNames.TotalSuperstarsCollected, 1);
        }

        if (this._givePoints == true) {
            const rules = puzzle.rules as QuickPlayGameRules;
            rules.increaseNumStars();

            let increase = this.increaseMultipler * rules.getNumStars();
            if (increase == 0) {
                increase = 1;
            }
            puzzle.score.addScoreFx(
                Math.ceil(this.baseWorth * (this._remainingTurns - 1) * increase),
                this.object,
                ScoreType.Score,
                0,
                false
            );
        }

        // Turn off countdown
        this._countDownMesh.active = false;

        // // called when this object explodes on the board.
        if (this.deathFX != null && this._givePoints == true) {
            const fx = BlobPrefabs.instance.spawn(this.deathFX, null, true);
            const p = this._boardObject.object.getPositionWorld();
            const r = this._boardObject.object.getRotationWorld();
            fx.setPositionWorld(p);
            fx.setRotationWorld(r);

            if (this._remainingTurns == this._explodeAfterTurn) {
                AudioDirector.instance.playOneShot(this.superStarSFX, true);
            } else {
                AudioDirector.instance.playOneShot(this.destroySFX, true);
            }
        }
        // it removed itself: you didnt get it in time
        else if (this.lostPowerupFX != null && this._givePoints == false) {
            const fx = BlobPrefabs.instance.spawn(this.lostPowerupFX, null, true);
            const p = this._boardObject.object.getPositionWorld();
            const r = this._boardObject.object.getRotationWorld();
            fx.setPositionWorld(p);
            fx.setRotationWorld(r);

            AudioDirector.instance.playOneShot(this.detonateSFX, true);
        }

        // if (AttachToBaseFX != null)
        // {
        // 	Destroy(AttachToBaseFX);
        // }

        this.object.destroy();
    }

    countdown(puzzle: Puzzle) {
        if (
            this._loadedTurn != -1 &&
            (this._loadedTurn == 0 || this._loadedTurn != puzzle.rules.turnsSinceStart - 1)
        ) {
            this._remainingTurns--;

            this._numberOfTurns++;
            this._updateTextureOffset();
            if (this._remainingTurns <= 1) {
                this._givePoints = false;
                this._countDownMesh.active = false;
                if (this._boardObject != null) {
                    this._boardObject.explodeLoadedObject();
                }
            }
        }
    }

    private _updateTextureOffset() {
        this._countDownMesh.material = this._countDownMaterials[15 - this._numberOfTurns];
    }
}
